import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      console.log('Auth state changed:', user);
      setCurrentUser(user);
      
      if (user) {
        try {
          console.log('Fetching user data for UID:', user.uid);
          
          // Retry with delays (race condition after registration or slow Firestore)
          let data = null;
          const delays = [300, 800, 1500];
          for (let i = 0; i < delays.length; i++) {
            await new Promise(resolve => setTimeout(resolve, delays[i]));
            data = await authService.getUserData(user.uid);
            if (data) break;
            console.log('No user data found, retry', i + 1, 'of', delays.length);
          }
          
          if (data) {
            console.log('User role:', data.role);
            console.log('User role type:', typeof data.role);
            setUserData(data);
          } else {
            console.warn('No user data found for UID after retry:', user.uid);
            setUserData(null);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          console.error('Error details:', {
            message: error.message,
            code: error.code,
            stack: error.stack
          });
          setUserData(null);
        }
      } else {
        console.log('No user authenticated, clearing user data');
        setUserData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    try {
      const user = await authService.login(email, password);
      return user;
    } catch (error) {
      throw error;
    }
  };

  const register = async (email, password, userData) => {
    try {
      const user = await authService.register(email, password, userData);
      return user;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      throw error;
    }
  };

  const updateUserData = async (newUserData) => {
    try {
      if (!currentUser) {
        throw new Error('No user logged in');
      }
      
      await authService.updateUserData(currentUser.uid, newUserData);
      
      // Update local state
      setUserData(prevData => ({
        ...prevData,
        ...newUserData
      }));
      
      return true;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    currentUser,
    userData,
    login,
    register,
    logout,
    updateUserData,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 